import { useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useTranslation } from "react-i18next"
import { cleanCancelRefund, fetchCancelRefundBreakdown, getCancelReasons, submitCancelAndRefund } from "store/bookings"
import { toaster } from "components/common/Toast"
import { numberToCurrency } from "helpers/string"

export const useCancelBookingModal = (booking, onConfirm) => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const [refundType, setRefundType] = useState("full")
  const [partialAmount, setPartialAmount] = useState("")
  const [partialAmountError, setPartialAmountError] = useState("")
  const [selectedReason, setSelectedReason] = useState(null)
  const [reasonError, setReasonError] = useState("")
  const [otherReason, setOtherReason] = useState("")
  const [otherReasonError, setOtherReasonError] = useState("")
  const [confirmOpen, setConfirmOpen] = useState(false)

  const { data: breakdownData, loading: breakdownLoading } = useSelector(
    (state) => state.bookings.cancelRefundBreakdown || {}
  )
  const { loading: submitLoading } = useSelector((state) => state.bookings.cancelRefundSubmit || {})
  const { data: cancelReasons, loading: reasonsLoading, failed: reasonsFailed } = useSelector(
    (state) => state.bookings.cancelReasons || {}
  )

  const isOtherSelected = useMemo(
    () => (cancelReasons || []).find((r) => r.id === selectedReason)?.reason?.toLowerCase() === "other",
    [cancelReasons, selectedReason]
  )

  const isRefundDisabled = breakdownData != null && (breakdownData.refundable_amount ?? 0) === 0

  useEffect(() => {
    dispatch(getCancelReasons())
    if (booking?.id) dispatch(fetchCancelRefundBreakdown(booking.id))
    return () => { dispatch(cleanCancelRefund()) }
  }, [dispatch, booking?.id])

  useEffect(() => {
    if (isRefundDisabled) {
      setRefundType("no_refund")
      setPartialAmount("")
      setPartialAmountError("")
    }
  }, [isRefundDisabled])

  const validatePartialAmount = (value) => {
    if (!value && value !== 0) return t("bookings.cancel_modal.amount_required")
    const num = Number.parseFloat(value)
    if (Number.isNaN(num) || num <= 0) return t("bookings.cancel_modal.amount_positive")
    if (num > (breakdownData?.amount_paid_by_guest || 0))
      return t("bookings.cancel_modal.amount_exceeds", { amount: numberToCurrency(breakdownData?.amount_paid_by_guest) })
    return ""
  }

  const handlePartialAmountChange = (e) => {
    const val = e.target.value
    setPartialAmount(val)
    setPartialAmountError(validatePartialAmount(val))
  }

  const handleRadioChange = (e) => {
    setRefundType(e.target.value)
    setPartialAmount("")
    setPartialAmountError("")
  }

  const handleSubmitClick = () => {
    if (!selectedReason) { setReasonError(t("bookings.cancel_modal.reason_required")); return }
    if (isOtherSelected && !otherReason.trim()) { setOtherReasonError(t("bookings.cancel_modal.other_reason_required")); return }
    if (refundType === "partial") {
      const error = validatePartialAmount(partialAmount)
      if (error) { setPartialAmountError(error); return }
    }
    setConfirmOpen(true)
  }

  const handleConfirmClick = async () => {
    setConfirmOpen(false)
    try {
      const payload = {
        cancel_reason_id: selectedReason,
        ...(isOtherSelected ? { other_reason: otherReason.trim() } : {}),
        refund_type: refundType,
        ...(refundType === "partial" ? { refund_amount: Number.parseFloat(partialAmount) } : {})
      }
      const result = await dispatch(submitCancelAndRefund(booking?.uuid || booking?.id, payload))
      if (result?.message) toaster.success(result.message)
      onConfirm?.()
    } catch (e) {
      console.error("Refund submission error:", e)
    }
  }

  return {
    refundType, partialAmount, partialAmountError,
    selectedReason, setSelectedReason, reasonError, setReasonError,
    otherReason, setOtherReason, otherReasonError, setOtherReasonError,
    breakdownData, breakdownLoading, submitLoading,
    cancelReasons, reasonsLoading, reasonsFailed,
    isOtherSelected, isRefundDisabled,
    confirmOpen, setConfirmOpen,
    handlePartialAmountChange, handleRadioChange, handleSubmitClick, handleConfirmClick
  }
}
